/**
 * custom-cart-attributes.js
 * CCC custom feature — syncs cart note-attributes to Shopify so they
 * appear in the admin "Additional details" panel on every order.
 *
 * When the cart contains:
 *  • Upcoming-roast coffee → sets upcoming_roast, roast_date, shipping_estimate
 *  • Preorder / partial-backorder items → sets preorder + preorder_dispatch_estimate
 *    (single detail line; preorder_items cleared so older carts drop the duplicate)
 *
 * Attributes are cleared when the relevant items are removed from the cart.
 *
 * Data source: reads the Liquid-rendered JSON element
 *   <script type="application/json" data-ccc-cart-state>
 * which is output by cart-products.liquid (present in both the cart page
 * and the cart drawer).
 *
 * Loaded on the cart page via sections/main-cart.liquid.
 */

import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';
import { cachedRoastDate, getShippingRange } from '@theme/custom-roast-manager';
import { getAdminDispatchEstimate } from '@theme/custom-dispatch-manager';

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Writes cart note-attributes via Shopify's AJAX API.
 * @param {Record<string, string>} attributes
 */
async function updateCartAttributes(attributes) {
  const res = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes }),
  });
  if (!res.ok) throw new Error(`/cart/update.js ${res.status}`);
  return res.json();
}

/**
 * Read the Liquid-rendered cart state JSON from the DOM.
 * cart-products.liquid outputs this in both cart page and cart drawer.
 * @returns {{ hasCoffee: boolean, hasPreorders: boolean, preorderItems: Array<{name: string, type: string, vendor: string, estimatedRestockDate?: string|null}> } | null}
 */
function readCartState() {
  const el = document.querySelector('script[data-ccc-cart-state]');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    console.warn('[CCC] Failed to parse cart state JSON', e);
    return null;
  }
}

// ─── Main sync function ─────────────────────────────────────────────────────

/** Track last synced JSON to avoid redundant /cart/update.js calls. */
let _lastSyncedJSON = '';

/**
 * Read the cart state from Liquid JSON, build the attribute map, and POST it.
 */
async function syncCartAttributes() {
  try {
    const state = readCartState();
    if (!state) return; // cart-products not rendered yet

    const attrs = {};

    // ── 1. Coffee (upcoming roast) ──────────────────────────────────────
    if (state.hasCoffee) {
      const info = await cachedRoastDate();
      attrs.upcoming_roast = 'true';

      if (info) {
        attrs.roast_date = info.formatted;
        attrs.shipping_estimate = getShippingRange(info.roastDate);
      } else {
        attrs.roast_date = 'next roast date';
        attrs.shipping_estimate = 'after roasting';
      }
    } else {
      attrs.upcoming_roast = '';
      attrs.roast_date = '';
      attrs.shipping_estimate = '';
    }

    // ── 2. Preorder / partial-backorder items ───────────────────────────
    if (state.hasPreorders && state.preorderItems.length > 0) {
      const dispatchParts = [];

      for (const item of state.preorderItems) {
        const name = item.name || 'Item';
        const lookupKey = item.type || item.vendor || '';
        const estimatedRestockDate = item.estimatedRestockDate || null;
        if (!lookupKey && !estimatedRestockDate) continue;

        const estimate = getAdminDispatchEstimate(lookupKey, { estimatedRestockDate });
        if (item.kind === 'partial_backorder') {
          const onHand = Number(item.onHand) || 0;
          const backordered = Number(item.backordered) || 0;
          dispatchParts.push(
            `${name}: ${onHand} in stock + ${backordered} backorder (${estimate})`
          );
        } else {
          dispatchParts.push(`${name}: ${estimate}`);
        }
      }

      attrs.preorder = 'true';
      // Clear legacy duplicate attribute (names alone); detail line includes product names.
      attrs.preorder_items = '';
      attrs.preorder_dispatch_estimate = dispatchParts.join(' | ');
    } else {
      attrs.preorder = '';
      attrs.preorder_items = '';
      attrs.preorder_dispatch_estimate = '';
    }

    // ── 3. Skip if nothing changed ──────────────────────────────────────
    const json = JSON.stringify(attrs);
    if (json === _lastSyncedJSON) return;

    // ── 4. Write to Shopify ─────────────────────────────────────────────
    await updateCartAttributes(attrs);
    _lastSyncedJSON = json;
    console.log('[CCC] Cart attributes synced:', attrs);

  } catch (err) {
    console.warn('[CCC] Cart attributes sync failed:', err);
  }
}

// ─── Event wiring ───────────────────────────────────────────────────────────

// Run on initial page load. Delay 150ms to let Liquid/morph finish
// populating the DOM (cart-products JSON must be rendered first).
setTimeout(syncCartAttributes, 150);

// Re-sync after cart morph completes (event.promise resolves after morphSection).
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(() => syncCartAttributes())
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[CCC] Cart attributes sync skipped:', error);
    });
});
